import React, { useEffect, useMemo } from "react";
import { X, Server, Package, CheckCircle, XCircle, Clock, PlayCircle, Shield, Info, Command, List } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface PatchMetrics {
    total: number;
    initiated: number;
    running: number;
    failed: number;
    successed: number;
}

interface PatchParameters {
    application_name: string;
    download_url: string;
    checksum: string;
    os_type: string;
    installer_type: string;
    installation_cmd: string;
}

interface PatchDetailsProps {
    isOpen: boolean;
    onClose: () => void;
    patchData: {
        id: string;
        job_id: string;
        organization_id: string;
        client_id: string;
        job_type: string;
        status: string;
        metrics: PatchMetrics;
        details: any; // Can be object or stringified JSON
        parameters: PatchParameters;
        created: string;
        updated: string;
        is_deleted: boolean;
    } | null;
}

const PatchDetails: React.FC<PatchDetailsProps> = ({ isOpen, onClose, patchData }) => {
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    // Safely parse the details object if it comes as a string
    const parsedDetails = useMemo(() => {
        if (!patchData?.details) return {};
        if (typeof patchData.details === 'string') {
            try {
                return JSON.parse(patchData.details);
            } catch (error) {
                console.error("Failed to parse patch details:", error);
                return {};
            }
        }
        return patchData.details;
    }, [patchData?.details]);

    const getOsIcon = (os?: string) => {
        if (!os) return null;
        const value = os.toLowerCase();

        if (value.includes("windows")) return "/windows.png";
        if (value.includes("mac") || value.includes("darwin") || value.includes("os x")) return "/mac.png";
        if (value.includes("linux") || value.includes("ubuntu") || value.includes("debian") || value.includes("centos"))
            return "/linux.svg";

        return null;
    };


    if (!patchData) return null;

    const formatDate = (dateString: string) => {
        if (!dateString) return "-";
        try {
            const utcString = dateString.endsWith("Z") ? dateString : `${dateString}Z`;
            const date = new Date(utcString);
            return date.toLocaleString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
            });
        } catch {
            return "Invalid Date";
        }
    };



    const getStatusIcon = (status: string) => {
        const s = status?.toLowerCase() || "";
        if (s.includes("init")) return <Clock size={12} className="text-blue-400" />;
        if (s.includes("run")) return <PlayCircle size={12} className="text-yellow-400" />;
        if (s.includes("comp") || s.includes("succ")) return <CheckCircle size={12} className="text-green-400" />;
        if (s.includes("fail")) return <XCircle size={12} className="text-red-500" />;
        return <Shield size={12} className="text-gray-200" />;
    };

    const getStatusStyle = (status: string) => {
        const s = status?.toLowerCase() || "";
        if (s.includes("init")) return "bg-blue-500/10 text-blue-400 border-blue-500/20";
        if (s.includes("run")) return "bg-yellow-500/10 text-yellow-500 border-yellow-500/20";
        if (s.includes("comp") || s.includes("succ")) return "bg-green-500/10 text-green-400 border-green-500/20";
        if (s.includes("fail")) return "bg-red-500/10 text-red-500 border-red-500/30 shadow-[0_0_8px_rgba(239,68,68,0.15)]";
        return "bg-gray-800 text-gray-200 border-gray-700";
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]"
                    />

                    {/* Sliding Panel - More Compact Width */}
                    <motion.div
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "100%" }}
                        transition={{ type: "spring", damping: 25, stiffness: 200 }}
                        className="fixed top-0 right-0 h-full w-full sm:w-[480px] md:w-[540px] lg:w-[600px] bg-gray-950 shadow-2xl z-[110] flex flex-col border-l border-gray-800"
                    >
                        {/* Header - Compact Padding */}
                        <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-3 flex items-center justify-between border-b border-gray-700 shadow-lg shrink-0">
                            <div className="flex items-center gap-2.5">
                                <div className="p-1.5 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg shadow-lg border border-white/10">
                                    <Package className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <h2 className="text-sm font-bold text-white tracking-tight leading-tight">Patch Insight</h2>
                                    <div className="flex items-center gap-1.5 mt-0.5">
                                        <div className={`px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-widest border ${getStatusStyle(patchData.status)}`}>
                                            {patchData.status}
                                        </div>
                                        <span className="text-gray-200 text-[9px] font-bold uppercase tracking-widest">ID: {patchData.job_id?.slice(0, 8)}</span>
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-1.5 hover:bg-gray-800 rounded-lg transition-all duration-200 text-gray-200 hover:text-white border border-transparent hover:border-gray-700"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scroll bg-gray-900/50">

                            {/* Stats Ribbon - Compact Grid */}
                            <div className="grid grid-cols-5 gap-2">
                                {[
                                    { label: "Total", val: patchData.metrics?.total, color: "text-gray-200", bg: "bg-gray-800" },
                                    { label: "Init", val: patchData.metrics?.initiated, color: "text-blue-400", bg: "bg-blue-500/10 border-blue-500/20" },
                                    { label: "Run", val: patchData.metrics?.running, color: "text-yellow-400", bg: "bg-yellow-500/10 border-yellow-500/20" },
                                    { label: "Fail", val: patchData.metrics?.failed, color: "text-red-500", bg: "bg-red-500/10 border-red-500/20" },
                                    { label: "Succ", val: patchData.metrics?.successed, color: "text-green-400", bg: "bg-green-500/10 border-green-500/20" },
                                ].map((s, i) => (
                                    <div key={i} className={`${s.bg} border border-gray-700 p-2 rounded-lg flex flex-col items-center justify-center shadow-sm`}>
                                        <span className={`text-sm font-black ${s.color} leading-none mb-1`}>{s.val || 0}</span>
                                        <span className="text-[8px] font-bold text-gray-200 uppercase tracking-widest">{s.label}</span>
                                    </div>
                                ))}
                            </div>

                            {/* Equal Height Group for App Details & Params using Grid auto-stretching */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
                                {/* Application Info */}
                                <div className="flex flex-col">
                                    <h3 className="text-[10px] font-black text-orange-500 uppercase tracking-[0.15em] flex items-center gap-1.5 mb-2">
                                        <Info size={12} /> App Details
                                    </h3>
                                    <div className="flex-1 bg-gray-900 border border-gray-800 p-3 rounded-xl flex flex-col justify-between space-y-3 shadow-inner">
                                        <div>
                                            <label className="text-[8px] font-bold text-gray-200 uppercase tracking-widest block mb-0.5">Name</label>
                                            <p className="text-[11px] font-bold text-gray-200 truncate" title={patchData.parameters?.application_name}>{patchData.parameters?.application_name || "-"}</p>
                                        </div>
                                        <div>
                                            <label className="text-[8px] font-bold text-gray-200 uppercase tracking-widest block mb-0.5">Download URL</label>
                                            <a href={patchData.parameters?.download_url} target="_blank" rel="noreferrer" className="text-[10px] text-blue-400 hover:underline break-all font-medium line-clamp-2" title={patchData.parameters?.download_url}>
                                                {patchData.parameters?.download_url}
                                            </a>
                                        </div>
                                        <div className="flex gap-4 pt-1">
                                            <div>
                                                <label className="text-[8px] font-bold text-gray-200 uppercase tracking-widest block mb-0.5">OS</label>
                                                <div className="flex items-center gap-1">
                                                    {getOsIcon(patchData.parameters?.os_type) && (
                                                        <img
                                                            src={getOsIcon(patchData.parameters?.os_type)!}
                                                            alt="os"
                                                            className="w-3 h-3 flex-shrink-0"
                                                        />
                                                    )}

                                                    <span className="text-[10px] font-mono text-lime-300 truncate">
                                                        {patchData.parameters?.os_type || "-"}
                                                    </span>
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-[8px] font-bold text-gray-200 uppercase tracking-widest block">Type</label>
                                                <span className="bg-gray-800 text-gray-300 text-[9px] font-black uppercase px-1.5 py-0.5 rounded border border-gray-700">
                                                    {patchData.parameters?.installer_type || "-"}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Deployment Params */}
                                <div className="flex flex-col">
                                    <h3 className="text-[10px] font-black text-orange-500 uppercase tracking-[0.15em] flex items-center gap-1.5 mb-2">
                                        <Command size={12} /> Parameters
                                    </h3>
                                    <div className="flex-1 bg-gray-900 border border-gray-800 p-3 rounded-xl flex flex-col justify-between space-y-3 shadow-inner">
                                        <div>
                                            <label className="text-[8px] font-bold text-gray-200 uppercase tracking-widest block mb-1">Install Command</label>
                                            <div className="bg-black/60 border border-gray-800 p-2 rounded-md text-[10px] font-mono text-gray-300 truncate" title={patchData.parameters?.installation_cmd}>
                                                {patchData.parameters?.installation_cmd || "-"}
                                            </div>
                                        </div>
                                        <div className="pt-1">
                                            <label className="text-[8px] font-bold text-gray-200 uppercase tracking-widest block mb-1">Checksum (SHA256)</label>
                                            <div className="bg-black/60 border border-gray-800 p-2 rounded-md text-[9px] font-mono text-gray-200 break-all leading-snug">
                                                {patchData.parameters?.checksum || "-"}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Assets List */}
                            <div className="space-y-2.5">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-[10px] font-black text-orange-500 uppercase tracking-[0.15em] flex items-center gap-1.5">
                                        <List size={12} /> Targeted Assets ({Object.keys(parsedDetails).length})
                                    </h3>
                                </div>

                                <div className="grid grid-cols-1 gap-2.5">
                                    {Object.entries(parsedDetails).map(([agentId, detail]: [string, any]) => (
                                        <div key={agentId} className={`bg-gray-900 border p-2.5 rounded-lg transition-colors group ${detail.status?.toLowerCase().includes('fail') ? 'border-red-500/30 bg-red-500/5' : 'border-gray-800 hover:border-gray-700'}`}>

                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2.5 min-w-0">
                                                    <div className="w-6 h-6 rounded bg-gray-800 border border-gray-700 flex items-center justify-center flex-shrink-0 group-hover:bg-gray-700 transition-colors">
                                                        <Server size={12} className="text-gray-200" />
                                                    </div>
                                                    <div className="min-w-0">
                                                        <p className="text-[11px] font-bold text-gray-200 truncate">{detail.hostname || "Unknown Host"}</p>

                                                    </div>

                                                </div>
                                                <div className="flex items-center gap-2.5 min-w-0">
                                                    <div className="min-w-0">
                                                        <p className="text-[11px] font-mono text-gray-200 truncate">IP: {detail.ip_address || "-"}</p>

                                                    </div>
                                                    <div className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest border flex items-center gap-1 shrink-0 ${getStatusStyle(detail.status)}`}>
                                                        {getStatusIcon(detail.status)}
                                                        {detail.status || "Unknown"}
                                                    </div>

                                                </div>
                                            </div>

                                            {/* Error/Info section compacted */}
                                            {detail.info && (
                                                <div className={`mt-2 text-[9px] font-mono p-1.5 rounded border custom-scroll overflow-y-auto whitespace-pre-wrap break-words ${detail.status?.toLowerCase().includes('fail')
                                                    ? 'bg-red-950/20 border-red-500/20 text-red-300'
                                                    : 'bg-black/40 border-gray-800 text-gray-200'
                                                    }`}>
                                                    {typeof detail.info === 'string' ? detail.info : JSON.stringify(detail.info, null, 2)}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Footer - Compact */}
                        <div className="p-3 bg-gray-900 border-t border-gray-800 flex items-center justify-between shrink-0">
                            <div className="flex flex-col">
                                <span className="text-[8px] font-bold text-gray-200 uppercase tracking-widest">Created On</span>
                                <span className="text-[10px] font-bold text-gray-300">{formatDate(patchData.created)}</span>
                            </div>
                            <button
                                onClick={onClose}
                                className="px-4 py-1.5 bg-gray-800 text-gray-300 rounded-lg font-bold text-[10px] uppercase tracking-widest hover:bg-gray-700 transition-all border border-gray-700"
                            >
                                Close Insight
                            </button>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default PatchDetails;